import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Calendar, ArrowUpDown, Filter } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import Footer from '../components/layout/Footer';
import { Card } from '../components/common/Card';
import { theme, media } from '../styles/theme';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background: linear-gradient(135deg, ${theme.colors.background} 0%, #F0F0F0 100%);
  animation: fadeIn 0.5s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  padding: ${theme.spacing.xl};
  padding-bottom: 80px;

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-left: 0;
    padding: ${theme.spacing.md};
    padding-top: 70px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
    padding-top: 70px;
  }
`;

const Header = styled.div`
  margin-bottom: ${theme.spacing.xl};
  
  h1 {
    font-size: ${theme.typography.fontSize['3xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
    margin-bottom: ${theme.spacing.xs};
  }
  
  p {
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.base};
  }

  ${media.down.sm} {
    h1 {
      font-size: ${theme.typography.fontSize['2xl']};
    }
  }
`;

const FiltersContainer = styled(Card)`
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
  display: flex;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
  align-items: center;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};

  ${media.down.sm} {
    padding: ${theme.spacing.md};
    gap: ${theme.spacing.sm};
  }
`;

const FilterButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${({ active }) => active ? theme.colors.primary : theme.colors.border};
  background: ${({ active }) => active ? theme.colors.primary : theme.colors.white};
  color: ${({ active }) => active ? theme.colors.white : theme.colors.text};
  cursor: pointer;
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${({ active }) => active ? theme.typography.fontWeight.semiBold : theme.typography.fontWeight.regular};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};

  &:hover {
    border-color: ${theme.colors.primary};
    background: ${({ active }) => active ? theme.colors.primary : `${theme.colors.primary}08`};
    transform: translateY(-1px);
  }
`;

const DateInput = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily};
  outline: none;
  transition: all 0.2s ease;
  background: ${theme.colors.white};

  &:focus {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}10;
  }
`;

const TableCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.sm};
`;

const TableContainer = styled.div`
  overflow-y: auto;
  overflow-x: auto;
  max-height: calc(100vh - 400px);
  min-height: 400px;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.colors.secondary};
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.border};
    border-radius: ${theme.borderRadius.md};
    
    &:hover {
      background: ${theme.colors.textSecondary};
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;

  ${media.down.sm} {
    min-width: 500px;
  }
`;

const TableHeader = styled.thead`
  background: ${theme.colors.background};
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableHeaderRow = styled.tr``;

const TableHeaderCell = styled.th`
  padding: ${theme.spacing.lg};
  text-align: left;
  font-weight: ${theme.typography.fontWeight.semiBold};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: ${({ sortable }) => sortable ? 'pointer' : 'default'};
  user-select: none;
  position: relative;
  white-space: nowrap;
  border-bottom: 2px solid ${theme.colors.border};

  &:hover {
    background: ${({ sortable }) => sortable ? `${theme.colors.primary}05` : 'transparent'};
  }

  svg {
    margin-left: ${theme.spacing.xs};
    width: 14px;
    height: 14px;
    opacity: ${({ sorted }) => sorted ? 1 : 0.4};
    transition: opacity 0.2s ease;
  }

  ${media.down.sm} {
    padding: ${theme.spacing.md};
    font-size: ${theme.typography.fontSize.xs};
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.border};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.background};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: ${theme.spacing.lg};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text};
  vertical-align: top;

  ${media.down.sm} {
    padding: ${theme.spacing.md};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const MenuItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const MenuItem = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
`;

const PaymentModeBadge = styled.span`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  background: ${({ mode }) => {
    if (mode === 'Cash') return `${theme.colors.success}20`;
    if (mode === 'Card') return `${theme.colors.primary}20`;
    if (mode === 'UPI') return `${theme.colors.accentTeal}20`;
    return `${theme.colors.textSecondary}20`;
  }};
  color: ${({ mode }) => {
    if (mode === 'Cash') return theme.colors.success;
    if (mode === 'Card') return theme.colors.primary;
    if (mode === 'UPI') return theme.colors.accentTeal;
    return theme.colors.textSecondary;
  }};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  display: inline-block;
`;

const SummaryStats = styled.div`
  display: flex;
  gap: ${theme.spacing.xl};
  margin-left: auto;
  align-items: center;

  ${media.down.sm} {
    margin-left: 0;
    width: 100%;
    justify-content: space-between;
    gap: ${theme.spacing.md};
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  ${media.down.sm} {
    align-items: flex-start;
  }
`;

const StatLabel = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${theme.spacing.xs};
`;

const StatValue = styled.strong`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.primary};
  font-weight: ${theme.typography.fontWeight.bold};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing['3xl']};
  color: ${theme.colors.textSecondary};
  
  p {
    font-size: ${theme.typography.fontSize.lg};
  }
`;

// Mock sales data with dates
const generateMockSalesData = () => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const menuItems = [
    ['Paneer Tikka', 'Butter Naan', 'Dal Makhani'],
    ['Chicken Biryani', 'Mango Lassi'],
    ['Samosa', 'Ice Cream', 'Gulab Jamun'],
    ['Butter Chicken', 'Garlic Naan', 'Raita'],
    ['Veg Thali', 'Lassi'],
    ['Tandoori Chicken', 'Naan', 'Salad'],
    ['Dosa', 'Sambar', 'Coconut Chutney'],
    ['Biryani', 'Raita', 'Pickle'],
  ];

  const paymentModes = ['Cash', 'Card', 'UPI'];

  const sales = [];
  
  // Today's sales
  for (let i = 0; i < 8; i++) {
    sales.push({
      id: `today-${i + 1}`,
      date: formatDate(today),
      tableNumber: Math.floor(Math.random() * 20) + 1,
      billAmount: Math.floor(Math.random() * 5000) + 500,
      paymentMode: paymentModes[Math.floor(Math.random() * paymentModes.length)],
      menuItems: menuItems[i % menuItems.length],
      timestamp: `${Math.floor(Math.random() * 12) + 1}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')} ${Math.random() > 0.5 ? 'AM' : 'PM'}`,
    });
  }

  // Yesterday's sales
  for (let i = 0; i < 6; i++) {
    sales.push({
      id: `yesterday-${i + 1}`,
      date: formatDate(yesterday),
      tableNumber: Math.floor(Math.random() * 20) + 1,
      billAmount: Math.floor(Math.random() * 5000) + 500,
      paymentMode: paymentModes[Math.floor(Math.random() * paymentModes.length)],
      menuItems: menuItems[i % menuItems.length],
      timestamp: `${Math.floor(Math.random() * 12) + 1}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')} ${Math.random() > 0.5 ? 'AM' : 'PM'}`,
    });
  }

  // Older sales (last 7 days)
  for (let day = 2; day <= 7; day++) {
    const date = new Date(today);
    date.setDate(date.getDate() - day);
    for (let i = 0; i < 4; i++) {
      sales.push({
        id: `day${day}-${i + 1}`,
        date: formatDate(date),
        tableNumber: Math.floor(Math.random() * 20) + 1,
        billAmount: Math.floor(Math.random() * 5000) + 500,
        paymentMode: paymentModes[Math.floor(Math.random() * paymentModes.length)],
        menuItems: menuItems[i % menuItems.length],
        timestamp: `${Math.floor(Math.random() * 12) + 1}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')} ${Math.random() > 0.5 ? 'AM' : 'PM'}`,
      });
    }
  }

  return sales;
};

const mockSalesData = generateMockSalesData();

function SalesData() {
  const [dateFilter, setDateFilter] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedSales = useMemo(() => {
    let filtered = [...mockSalesData];

    // Apply date filter
    if (dateFilter === 'today') {
      const today = getTodayDate();
      filtered = filtered.filter(sale => sale.date === today);
    } else if (dateFilter === 'yesterday') {
      const yesterday = getYesterdayDate();
      filtered = filtered.filter(sale => sale.date === yesterday);
    } else if (dateFilter === 'custom') {
      if (startDate && endDate) {
        filtered = filtered.filter(sale => sale.date >= startDate && sale.date <= endDate);
      } else if (startDate) {
        filtered = filtered.filter(sale => sale.date >= startDate);
      } else if (endDate) {
        filtered = filtered.filter(sale => sale.date <= endDate);
      }
    }

    // Apply sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];

        if (sortField === 'billAmount') {
          aVal = Number(aVal);
          bVal = Number(bVal);
        } else if (sortField === 'tableNumber') {
          aVal = Number(aVal);
          bVal = Number(bVal);
        } else if (sortField === 'date') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }

        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [dateFilter, startDate, endDate, sortField, sortDirection]);

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    if (filter !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const totalSales = filteredAndSortedSales.reduce((sum, sale) => sum + sale.billAmount, 0);

  return (
    <Container>
      <Sidebar />
      <MainContent>
        <Header>
          <h1>Sales Data</h1>
        </Header>

        <FiltersContainer>
          <FilterButton
            active={dateFilter === 'today'}
            onClick={() => handleDateFilterChange('today')}
          >
            <Calendar size={16} />
            Today
          </FilterButton>
          <FilterButton
            active={dateFilter === 'yesterday'}
            onClick={() => handleDateFilterChange('yesterday')}
          >
            <Calendar size={16} />
            Yesterday
          </FilterButton>
          <FilterButton
            active={dateFilter === 'custom'}
            onClick={() => handleDateFilterChange('custom')}
          >
            <Filter size={16} />
            Custom Range
          </FilterButton>
          
          {dateFilter === 'custom' && (
            <>
              <DateInput
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
              />
              <span style={{ color: theme.colors.textSecondary }}>to</span>
              <DateInput
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
              />
            </>
          )}

          <SummaryStats>
            <StatItem>
              <StatLabel>Total Sales</StatLabel>
              <StatValue>₹{totalSales.toLocaleString('en-IN')}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Records</StatLabel>
              <StatValue>{filteredAndSortedSales.length}</StatValue>
            </StatItem>
          </SummaryStats>
        </FiltersContainer>

        <TableCard>
          <TableContainer>
            {filteredAndSortedSales.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell 
                      sortable 
                      sorted={sortField === 'date'}
                      onClick={() => handleSort('date')}
                    >
                      Date & Time
                      <ArrowUpDown size={14} />
                    </TableHeaderCell>
                    <TableHeaderCell 
                      sortable 
                      sorted={sortField === 'tableNumber'}
                      onClick={() => handleSort('tableNumber')}
                    >
                      Table Number
                      <ArrowUpDown size={14} />
                    </TableHeaderCell>
                    <TableHeaderCell 
                      sortable 
                      sorted={sortField === 'billAmount'}
                      onClick={() => handleSort('billAmount')}
                    >
                      Bill Amount
                      <ArrowUpDown size={14} />
                    </TableHeaderCell>
                    <TableHeaderCell>Payment Mode</TableHeaderCell>
                    <TableHeaderCell>Menu Items</TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedSales.map((sale) => (
                    <TableRow key={sale.id}>
                      <TableCell>
                        {new Date(sale.date).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                        <br />
                        <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                          {sale.timestamp}
                        </span>
                      </TableCell>
                      <TableCell>{sale.tableNumber}</TableCell>
                      <TableCell>
                        <strong>₹{sale.billAmount.toLocaleString('en-IN')}</strong>
                      </TableCell>
                      <TableCell>
                        <PaymentModeBadge mode={sale.paymentMode}>
                          {sale.paymentMode}
                        </PaymentModeBadge>
                      </TableCell>
                      <TableCell>
                        <MenuItemsList>
                          {sale.menuItems.map((item, idx) => (
                            <MenuItem key={idx}>• {item}</MenuItem>
                          ))}
                        </MenuItemsList>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <EmptyState>
                <p>No sales data found for the selected date range.</p>
              </EmptyState>
            )}
          </TableContainer>
        </TableCard>
      </MainContent>
      <Footer />
    </Container>
  );
}

export default SalesData;

